/**
 * Quotation PDF Generator
 * Generates a 2-page professional Quotation PDF using pdfmake.
 * Uses existing offer, quotation_items, quotation_summary - no recalculation.
 */
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const formatCurrency = (val) =>
    val != null
        ? `₹ ${Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : "₹ 0.00";

const formatDate = (d) => {
    if (!d) return "-";
    try {
        const date = new Date(d);
        return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
    } catch {
        return "-";
    }
};

const urlToDataUri = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) return null;
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
};

export async function generateQuotationPdf(offer, quotationItems, quotationSummary) {
    const logoData = await urlToDataUri("/fuzi_classic_elevators_pvt_ltd_cover.jpg");
    const summary = quotationSummary || {};
    const items = quotationItems || [];

    const page1Content = [
        logoData
            ? { image: logoData, width: 280, alignment: "center", margin: [0, 0, 0, 16] }
            : { text: "FUZI ELEVATORS", fontSize: 24, bold: true, alignment: "center", margin: [0, 0, 0, 16] },
        { text: "FUZI ELEVATORS", fontSize: 22, bold: true, alignment: "center", margin: [0, 0, 0, 4] },
        { text: "Engineering Vertical Excellence", fontSize: 12, alignment: "center", margin: [0, 0, 0, 24] },
        {
            text: "Fuzi Elevators is a leading elevator manufacturing company with decades of expertise in precision engineering and vertical transportation solutions. We specialize in designing and delivering custom elevator solutions that meet the highest standards of safety, quality, and performance. Our commitment to innovation, rigorous quality assurance, and customer-centric approach has established us as a trusted partner for residential, commercial, and industrial projects. We integrate modern technology with proven engineering practices to ensure every installation exceeds expectations.",
            fontSize: 10,
            alignment: "justify",
            lineHeight: 1.4,
            margin: [0, 0, 0, 20],
        },
        { text: "Mission", fontSize: 12, bold: true, margin: [0, 16, 0, 6] },
        {
            text: "To deliver exceptional vertical transportation solutions through innovation, quality, and unwavering customer commitment.",
            fontSize: 10,
            margin: [0, 0, 0, 12],
        },
        { text: "Vision", fontSize: 12, bold: true, margin: [0, 0, 0, 6] },
        {
            text: "To be the preferred choice for elevator solutions, recognized for excellence in engineering and customer satisfaction.",
            fontSize: 10,
            margin: [0, 0, 0, 12],
        },
        { text: "Core Strengths", fontSize: 12, bold: true, margin: [0, 0, 0, 8] },
        {
            ul: [
                "Precision engineering and manufacturing expertise",
                "Custom solutions tailored to project requirements",
                "Full safety compliance and quality certification",
                "Modern technology integration",
                "Dedicated customer support and service",
            ],
            fontSize: 10,
            margin: [0, 0, 0, 0],
        },
    ];

    const tableBody = [
        [
            { text: "Description", style: "tableHeader", fillColor: "#f0f0f0" },
            { text: "Qty", style: "tableHeader", fillColor: "#f0f0f0" },
            { text: "Rate", style: "tableHeader", fillColor: "#f0f0f0" },
            { text: "Amount", style: "tableHeader", fillColor: "#f0f0f0" },
        ],
    ];

    items.forEach((item) => {
        tableBody.push([
            item.item_name || "-",
            String(item.quantity ?? "-"),
            formatCurrency(item.rate),
            formatCurrency(item.line_total),
        ]);
    });

    const grandTotal = summary.final_offer ?? summary.grand_total ?? 0;

    const page2Content = [
        { text: "QUOTATION", fontSize: 18, bold: true, margin: [0, 0, 0, 16] },
        {
            stack: [
                { text: `Offer No: ${offer?.offer_ref_no ?? offer?.job_no ?? "-"}`, fontSize: 10 },
                { text: `Client: ${offer?.company_name ?? offer?.lead_name ?? "-"}`, fontSize: 10 },
                { text: `Address: ${offer?.site_address ?? "-"}`, fontSize: 10 },
                { text: `Date: ${formatDate(offer?.offer_date)}`, fontSize: 10 },
                ...(offer?.offer_name ? [{ text: `Project: ${offer.offer_name}`, fontSize: 10 }] : []),
            ],
            margin: [0, 0, 0, 16],
        },
        { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }], margin: [0, 0, 0, 16] },
        {
            table: {
                headerRows: 1,
                widths: ["*", 50, 80, 90],
                body: tableBody,
            },
            layout: "lightHorizontalLines",
            margin: [0, 0, 0, 24],
        },
        {
            columns: [
                { text: " ", width: "*" },
                {
                    width: "auto",
                    table: {
                        widths: ["auto", "auto"],
                        body: [
                            ["Subtotal", formatCurrency(summary.subtotal)],
                            ["Margin", formatCurrency(summary.margin_amount)],
                            ["Discount", formatCurrency(summary.discount_amount)],
                            [`GST (${summary.gst_percentage ?? 0}%)`, formatCurrency(summary.gst_amount)],
                            [{ text: "GRAND TOTAL", bold: true, fontSize: 12 }, { text: formatCurrency(grandTotal), bold: true, fontSize: 12 }],
                        ],
                    },
                    layout: "noBorders",
                },
            ],
            margin: [0, 0, 0, 32],
        },
        { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }], margin: [0, 0, 0, 16] },
        {
            text: "Thank you for your business. We look forward to serving you.",
            fontSize: 10,
            italics: true,
            margin: [0, 0, 0, 4],
        },
        {
            text: "Terms: Prices valid for 30 days. Subject to terms and conditions.",
            fontSize: 9,
            color: "#666",
            margin: [0, 0, 0, 4],
        },
        {
            text: "Fuzi Elevators | Contact: support@fuzielevators.com",
            fontSize: 9,
            color: "#666",
        },
    ];

    const docDefinition = {
        pageSize: "A4",
        pageMargins: [40, 40, 40, 40],
        styles: {
            tableHeader: { fontSize: 10, bold: true },
        },
        content: [
            ...page1Content,
            { text: "", pageBreak: "after" },
            ...page2Content,
        ],
    };

    const fileName = `Quotation_${offer?.offer_ref_no ?? offer?.job_no ?? "Offer"}_${formatDate(new Date()).replace(/\//g, "-")}.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
}
